import React, { useState, useEffect } from 'react';
import { Course } from '../types';
import { getPublicCourses } from '../services/firebase';
import { Icons } from './ui/Icons';
import { formatAuthorName } from '../utils/formatters';
import { Pagination, getPaginatedItems, getTotalPages, ITEMS_PER_PAGE } from './ui/Pagination';

interface PublicCourseGalleryProps {
  onCourseSelect: (course: Course) => void;
  embedded?: boolean;
}

export const PublicCourseGallery: React.FC<PublicCourseGalleryProps> = ({ onCourseSelect, embedded = false }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadPublicCourses = async () => {
      try {
        setLoading(true);
        const publicCourses = await getPublicCourses();
        setCourses(publicCourses);
      } catch (err) {
        console.error('Failed to load public courses:', err);
        setError('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };

    loadPublicCourses();
  }, []);

  if (loading) {
    return (
      <div className={embedded ? "py-12 flex justify-center" : "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center"}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? "py-12 text-center" : "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6"}>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50"}>
      {/* Content */}
      <div className={embedded ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {courses.length === 0 ? (
          <div className="text-center py-12">
            <Icons.BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Public Courses Yet</h2>
            <p className="text-gray-500 mb-6">Be the first to create and share a public course!</p>
            <button
              onClick={() => {}}
              className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Get Started
            </button>
          </div>
        ) : (
          <div>
            {!embedded && (
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {courses.length} {courses.length === 1 ? 'Course' : 'Courses'} Available
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getPaginatedItems(courses, currentPage).map((course) => (
                <div
                  key={course.id}
                  onClick={() => onCourseSelect(course)}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer overflow-hidden group"
                >
                  {/* Cover Image */}
                  {course.coverImage ? (
                    <div className="h-48 overflow-hidden relative">
                      <img
                        src={course.coverImage}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        style={{
                          objectPosition: course.coverImagePosition
                            ? `${course.coverImagePosition.x}% ${course.coverImagePosition.y}%`
                            : 'center'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Icons.BookOpen className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {course.description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Icons.BookOpen className="w-3.5 h-3.5" />
                        {course.studyIds?.length || 0} {course.studyIds?.length === 1 ? 'study' : 'studies'}
                      </span>
                    </div>

                    {/* Tags */}
                    {course.tags && course.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {course.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {course.tags.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{course.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* View Button */}
                    <button
                      className="w-full py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-bold text-sm shadow-md flex items-center justify-center gap-2 group-hover:bg-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCourseSelect(course);
                      }}
                    >
                      Start Course Free <Icons.Next className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={getTotalPages(courses.length)}
              totalItems={courses.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
              itemLabel="courses"
            />
          </div>
        )}
      </div>
    </div>
  );
};
